import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useStoreSettings } from '../hooks/useStoreSettings';
import { FavoriteProvider } from './FavoriteContext';
import { CartProvider } from './CartContext';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

const StoreContext = createContext(null);

export const StoreProvider = ({ children, storeData }) => {
    const { i18n } = useTranslation();
    const { slug } = useParams();
    // LayoutStore already initializes settings; pass slug so this never writes without store scope
    const { isInitialized, settingsLoaded } = useStoreSettings(storeData, !storeData, slug);

    // ✅ state للغة الحالية - يتم تحديثه من localStorage
    const [currentLang, setCurrentLang] = useState(() => {
        if (!slug) return 'ar';
        const savedLang = localStorage.getItem(`store_i18nextLng-${slug}`);
        return savedLang || 'ar';
    });

    // ✅ مزامنة اللغة عند تغيير slug أو تحميل البيانات
    useEffect(() => {
        if (!slug) return;
        
        const savedLang = localStorage.getItem(`store_i18nextLng-${slug}`);
        if (savedLang && savedLang !== currentLang) {
            
            setCurrentLang(savedLang);
            
            // تطبيق اللغة إذا كانت مختلفة
            if (i18n.language !== savedLang) {
                i18n.changeLanguage(savedLang);
                document.documentElement.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
                document.documentElement.lang = savedLang;
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, isInitialized]); // ✅ يعمل عند تغيير slug أو بعد التهيئة

    // Apply + persist language synchronously in storage before i18n settles,
    // so any remount/init that races the home refetch still restores this choice.
    const changeLanguage = useCallback((newLang) => {
        if (!slug) {
            console.warn('[StoreProvider] No slug available for language change');
            return Promise.resolve();
        }

        if (newLang !== 'ar' && newLang !== 'en') {
            return Promise.resolve();
        }

        // 1️⃣ Persist before apply — survives remount / useStoreSettings re-init
        localStorage.setItem(`store_i18nextLng-${slug}`, newLang);
        localStorage.setItem('i18nextLng', newLang);
        localStorage.setItem(`lang_user_changed-${slug}`, 'true');

        try {
            const storeSettings = JSON.parse(
                localStorage.getItem(`store_settings-${slug}`) || '{}'
            );
            storeSettings.lang = newLang;
            localStorage.setItem(`store_settings-${slug}`, JSON.stringify(storeSettings));
        } catch (error) {
            console.error('[StoreProvider] Error updating store_settings:', error);
        }

        document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = newLang;
        setCurrentLang(newLang);

        return i18n.changeLanguage(newLang);
    }, [slug, i18n]);

    return (
        <StoreContext.Provider
            value={{
                isInitialized,
                settingsLoaded,
                storeId: storeData?.id,
                storeData,
                type: storeData?.type,
                isMall: storeData?.type === 'mall',
                isBusiness: storeData?.type === 'business',
                whatsappNumber: storeData?.cart_whatssapp_number,
                packageType: storeData?.package_type || 'free',
                hasPrivatePackage: storeData?.has_private_package === true,
                currentLang,
                defaultLang: storeData?.lang || 'ar',
                isArabic: currentLang === 'ar',
                isEnglish: currentLang === 'en',
                changeLanguage
            }}
        >
            <FavoriteProvider>
                <CartProvider>
                    {children}
                </CartProvider>
            </FavoriteProvider>
        </StoreContext.Provider>
    );
};

export const useStore = () => {
    const context = useContext(StoreContext);

    // ✅ بدلاً من throw error، إرجاع null عند عدم وجود Provider
    if (!context) {
        // يمكنك استخدام console.warn للتنبيه فقط
        // console.warn('useStore called outside StoreProvider');
        return null;
    }

    return context;
};
